import React, { useEffect, useRef, useState, useCallback } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { useDispatch, useSelector } from "react-redux";
import { locationActions } from "../../actions/LocationActions";

// Custom debounce hook
function useDebounce(callback, delay) {
  const timeoutRef = useRef(null);

  const debouncedCallback = useCallback(
    (...args) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay]
  );

  return debouncedCallback;
}

const LocationMaster = () => {
  const dispatch = useDispatch();
  const toast = useRef(null);

  const [isAdd, setIsAdd] = useState(true);
  const [showStateDialog, setShowStateDialog] = useState(false);
  const [showDistrictDialog, setShowDistrictDialog] = useState(false);
  const [showCityDialog, setShowCityDialog] = useState(false);

  const [regionList, setRegionList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [districtList, setDistrictList] = useState([]);
  const [locationData, setLocationData] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const [selectedRegionForState, setSelectedRegionForState] = useState(null);
  const [newStateName, setNewStateName] = useState("");

  const [selectedRegionForDistrict, setSelectedRegionForDistrict] = useState(null);
  const [selectedStateForDistrict, setSelectedStateForDistrict] = useState(null);
  const [newDistrictName, setNewDistrictName] = useState("");

  const [selectedRegionForCity, setSelectedRegionForCity] = useState(null);
  const [selectedStateForCity, setSelectedStateForCity] = useState(null);
  const [selectedDistrictForCity, setSelectedDistrictForCity] = useState(null);
  const [newCityName, setNewCityName] = useState("");

  const [formError, setFormError] = useState({});
  const [pagination, setPagination] = useState({
    first: 0,
    rows: 10,
    page: 0,
    totalRecords: 0,
    search: "",
    sortField: "",
    sortOrder: "ASC",
  });

  const pageSizeOptions = [5, 10, 20, 50, 100];

  const { regions } = useSelector((state) => state.location || {});

  useEffect(() => {
    const userPages = sessionStorage.getItem("assignedPermissionsForUser");
    if (userPages) {
      try {
        const assignedPermissions = JSON.parse(userPages);
        const pageName = window.location.pathname.split("/").pop();
        const permission = assignedPermissions?.assigned?.find(
          (x) => x.ActionName === pageName
        );
        setIsAdd(permission?.IsAdd ?? false);
      } catch {
        setIsAdd(false);
      }
    }

    setLoading(true);
    Promise.all([
      dispatch(locationActions.getAllRegions()).then((data) => {
        setRegionList(data || []);
      }),
      fetchLocationData(),
    ]).finally(() => setLoading(false));
  }, [
    dispatch,
    pagination.page,
    pagination.rows,
    pagination.search,
    pagination.sortField,
    pagination.sortOrder,
  ]);

  const fetchLocationData = () => {
    setLoading(true);
    const request = {
      search: pagination.search,
      pageNumber: pagination.page + 1,
      pageSize: pagination.rows,
      sortField: pagination.sortField,
      sortOrder: pagination.sortOrder,
    };
    return dispatch(locationActions.GetAllLocation(request))
      .then((response) => {
        if (response.ResponseCode === "200") {
          if (response.ResponseData && response.ResponseData.length > 0) {
            setLocationData(response.ResponseData);
            setPagination((prev) => ({
              ...prev,
              totalRecords: response.ResponseData[0]?.TotalRecords || 0,
            }));
          } else {
            setLocationData([]);
            setPagination((prev) => ({
              ...prev,
              totalRecords: 0,
              first: 0,
            }));
            toast.current.show({
              severity: "info",
              summary: "No Results",
              detail: "Location Not Available",
            });
          }
        } else {
          setLocationData([]);
          setPagination((prev) => ({
            ...prev,
            totalRecords: 0,
            first: 0,
          }));
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail: response.ResponseMessage || "Failed to load location data",
          });
        }
      })
      .catch((error) => {
        setLocationData([]);
        setPagination((prev) => ({
          ...prev,
          totalRecords: 0,
          first: 0,
        }));
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: error.message || "Failed to load location data",
        });
      })
      .finally(() => setLoading(false));
  };

  const loadStates = (regionId, setTargetList) => {
    if (regionId) {
      dispatch(locationActions.getStateDDL(regionId)).then((data) =>
        setTargetList(data || [])
      );
    } else {
      setTargetList([]);
    }
  };

  const loadDistricts = (stateId) => {
    if (stateId) {
      dispatch(locationActions.getDistrictDDL(stateId)).then((data) =>
        setDistrictList(data || [])
      );
    } else {
      setDistrictList([]);
    }
  };

  const validate = (fields) => {
    const errors = {};
    fields.forEach(([key, value, message]) => {
      if (!value || (typeof value === "string" && !value.trim())) {
        errors[key] = message;
      }
    });
    setFormError(errors);
    return Object.keys(errors).length === 0;
  };

  const submitHandler = (type) => {
    let payload = {};
    if (type === "state") {
      if (
        !validate([
          ["selectedRegionForState", selectedRegionForState, "Select region"],
          ["newStateName", newStateName, "Enter state name"],
        ])
      )
        return;
      payload = { id: selectedRegionForState, name: newStateName };
      setLoading(true);
      dispatch(locationActions.addState(payload))
        .then(() => {
          toast.current.show({ severity: "success", summary: "Added", detail: "State added" });
          setShowStateDialog(false);
          resetStateForm();
          fetchLocationData();
        })
        .catch((error) => {
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail: error.message || "Failed to add state",
          });
        })
        .finally(() => setLoading(false));
    } else if (type === "district") {
      if (
        !validate([
          ["selectedRegionForDistrict", selectedRegionForDistrict, "Select region"],
          ["selectedStateForDistrict", selectedStateForDistrict, "Select state"],
          ["newDistrictName", newDistrictName, "Enter district name"],
        ])
      )
        return;
      payload = { id: selectedStateForDistrict, name: newDistrictName };
      setLoading(true);
      dispatch(locationActions.addDistrict(payload))
        .then(() => {
          toast.current.show({ severity: "success", summary: "Added", detail: "District added" });
          setShowDistrictDialog(false);
          resetDistrictForm();
          fetchLocationData();
        })
        .catch((error) => {
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail: error.message || "Failed to add district",
          });
        })
        .finally(() => setLoading(false));
    } else if (type === "city") {
      if (
        !validate([
          ["selectedRegionForCity", selectedRegionForCity, "Select region"],
          ["selectedStateForCity", selectedStateForCity, "Select state"],
          ["selectedDistrictForCity", selectedDistrictForCity, "Select district"],
          ["newCityName", newCityName, "Enter city name"],
        ])
      )
        return;
      payload = { id: selectedDistrictForCity, name: newCityName };
      setLoading(true);
      dispatch(locationActions.AddTown(payload))
        .then(() => {
          toast.current.show({ severity: "success", summary: "Added", detail: "City/Town added" });
          setShowCityDialog(false);
          resetCityForm();
          fetchLocationData();
        })
        .catch((error) => {
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail: error.message || "Failed to add city/town",
          });
        })
        .finally(() => setLoading(false));
    }
  };

  const resetStateForm = () => {
    setSelectedRegionForState(null);
    setNewStateName("");
    setFormError({});
  };

  const resetDistrictForm = () => {
    setSelectedRegionForDistrict(null);
    setSelectedStateForDistrict(null);
    setNewDistrictName("");
    setStateList([]);
    setFormError({});
  };

  const resetCityForm = () => {
    setSelectedRegionForCity(null);
    setSelectedStateForCity(null);
    setSelectedDistrictForCity(null);
    setNewCityName("");
    setStateList([]);
    setDistrictList([]);
    setFormError({});
  };

  const onPage = (event) => {
    setLoading(true);
    setPagination((prev) => ({
      ...prev,
      first: event.first,
      rows: event.rows,
      page: event.page,
    }));
  };

  const onSort = (event) => {
    setLoading(true);
    setPagination((prev) => ({
      ...prev,
      sortField: event.sortField,
      sortOrder: event.sortOrder === 1 ? "ASC" : "DESC",
      first: 0,
      page: 0,
    }));
  };

  const handleSearch = useDebounce((value) => {
    setGlobalFilter(value);
    setPagination((prev) => ({
      ...prev,
      search: value,
      first: 0,
      page: 0,
    }));
  }, 500);

  const onSearchChange = (e) => {
    const value = e.target.value || "";
    setGlobalFilter(value);
    handleSearch(value);
  };

  const renderDialogForm = (type) => {
    let content = null;
    if (type === "state") {
      content = (
        <>
          <Dropdown
            value={selectedRegionForState}
            options={regionList}
            onChange={(e) => {
              setSelectedRegionForState(e.value);
              setFormError((prev) => ({ ...prev, selectedRegionForState: null }));
            }}
            optionLabel="label"
            optionValue="value"
            placeholder="Select Region*"
            className="w-100 mb-3"
            filter
          />
          {formError.selectedRegionForState && <div className="error">{formError.selectedRegionForState}</div>}
          <input
            type="text"
            placeholder="State Name*"
            className="w-100 p-inputtext p-component mb-3"
            value={newStateName}
            onChange={(e) => {
              setNewStateName(e.target.value);
              setFormError((prev) => ({ ...prev, newStateName: null }));
            }}
          />
          {formError.newStateName && <div className="error">{formError.newStateName}</div>}
        </>
      );
    } else if (type === "district") {
      content = (
        <>
          <Dropdown
            value={selectedRegionForDistrict}
            options={regionList}
            onChange={(e) => {
              setSelectedRegionForDistrict(e.value);
              loadStates(e.value, setStateList);
              setSelectedStateForDistrict(null);
              setFormError((prev) => ({ ...prev, selectedRegionForDistrict: null }));
            }}
            optionLabel="label"
            optionValue="value"
            placeholder="Select Region*"
            className="w-100 mb-3"
            filter
          />
          {formError.selectedRegionForDistrict && <div className="error">{formError.selectedRegionForDistrict}</div>}

          <Dropdown
            value={selectedStateForDistrict}
            options={stateList}
            onChange={(e) => {
              setSelectedStateForDistrict(e.value);
              setFormError((prev) => ({ ...prev, selectedStateForDistrict: null }));
            }}
            optionLabel="label"
            optionValue="value"
            placeholder="Select State*"
            className="w-100 mb-3"
            filter
          />
          {formError.selectedStateForDistrict && <div className="error">{formError.selectedStateForDistrict}</div>}

          <input
            type="text"
            placeholder="District Name*"
            className="w-100 p-inputtext p-component mb-3"
            value={newDistrictName}
            onChange={(e) => {
              setNewDistrictName(e.target.value);
              setFormError((prev) => ({ ...prev, newDistrictName: null }));
            }}
          />
          {formError.newDistrictName && <div className="error">{formError.newDistrictName}</div>}
        </>
      );
    } else if (type === "city") {
      content = (
        <>
          <Dropdown
            value={selectedRegionForCity}
            options={regionList}
            onChange={(e) => {
              setSelectedRegionForCity(e.value);
              loadStates(e.value, setStateList);
              setDistrictList([]);
              setSelectedStateForCity(null);
              setSelectedDistrictForCity(null);
              setFormError((prev) => ({ ...prev, selectedRegionForCity: null }));
            }}
            optionLabel="label"
            optionValue="value"
            placeholder="Select Region*"
            className="w-100 mb-3"
            filter
          />
          {formError.selectedRegionForCity && <div className="error">{formError.selectedRegionForCity}</div>}

          <Dropdown
            value={selectedStateForCity}
            options={stateList}
            onChange={(e) => {
              setSelectedStateForCity(e.value);
              loadDistricts(e.value);
              setSelectedDistrictForCity(null);
              setFormError((prev) => ({ ...prev, selectedStateForCity: null }));
            }}
            optionLabel="label"
            optionValue="value"
            placeholder="Select State*"
            className="w-100 mb-3"
            filter
          />
          {formError.selectedStateForCity && <div className="error">{formError.selectedStateForCity}</div>}

          <Dropdown
            value={selectedDistrictForCity}
            options={districtList}
            onChange={(e) => {
              setSelectedDistrictForCity(e.value);
              setFormError((prev) => ({ ...prev, selectedDistrictForCity: null }));
            }}
            optionLabel="label"
            optionValue="value"
            placeholder="Select District*"
            className="w-100 mb-3"
            filter
          />
          {formError.selectedDistrictForCity && <div className="error">{formError.selectedDistrictForCity}</div>}

          <input
            type="text"
            placeholder="City/Town Name*"
            className="w-100 p-inputtext p-component mb-3"
            value={newCityName}
            onChange={(e) => {
              setNewCityName(e.target.value);
              setFormError((prev) => ({ ...prev, newCityName: null }));
            }}
          />
          {formError.newCityName && <div className="error">{formError.newCityName}</div>}
        </>
      );
    }

    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submitHandler(type);
        }}
        autoComplete="off"
      >
        {content}
        <div className="mt-4">
          <small>Fields marked with * are required</small>
        </div>
      </form>
    );
  };

  const tableHeader = (
    <div className="table-header row">
      <div className="col-md-6">
        <h4 className="m-0">Location Master</h4>
      </div>
      <div className="col-md-6 text-right">
        <span className="p-input-icon-left mr-2">
          <i className="pi pi-search" />
          <InputText
            type="search"
            value={globalFilter}
            onChange={onSearchChange}
            placeholder="Search Locations"
          />
        </span>
      </div>
    </div>
  );

  const stateColumnHeader = (
    <div className="column-header">
      <span>State Name</span>
      {isAdd && (
        <Button
          className="p-button-sm ml-2"
          icon="pi pi-plus"
          label="Add State"
          style={{ backgroundColor: "#87CEFA", borderColor: "#87CEFA", color: "#333" }}
          onClick={() => setShowStateDialog(true)}
        />
      )}
    </div>
  );

  const districtColumnHeader = (
    <div className="column-header">
      <span>District Name</span>
      {isAdd && (
        <Button
          className="p-button-sm ml-2"
          icon="pi pi-plus"
          label="Add District"
          style={{ backgroundColor: "#87CEFA", borderColor: "#87CEFA", color: "#333" }}
          onClick={() => setShowDistrictDialog(true)}
        />
      )}
    </div>
  );

  const townColumnHeader = (
    <div className="column-header">
      <span>Town Name</span>
      {isAdd && (
        <Button
          className="p-button-sm ml-2"
          icon="pi pi-plus"
          label="Add City/Town"
          style={{ backgroundColor: "#87CEFA ", borderColor: "#87CEFA", color: "#333" }}
          onClick={() => setShowCityDialog(true)}
        />
      )}
    </div>
  );

  return (
    <div className="p-fluid dash-margin">
      <Toast ref={toast} />
      <div className="card">
        {loading && (
          <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 1000 }}>
            <ProgressSpinner style={{ width: "50px", height: "50px" }} />
          </div>
        )}
        <DataTable
          value={locationData}
          stripedRows
          scrollable
          scrollHeight="500px"
          responsiveLayout="scroll"
          header={tableHeader}
          emptyMessage="No locations found."
          sortMode="single"
          removableSort
          paginator
          paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
          rows={pagination.rows}
          rowsPerPageOptions={pageSizeOptions}
          totalRecords={pagination.totalRecords}
          lazy
          first={pagination.first}
          onPage={onPage}
          onSort={onSort}
          sortField={pagination.sortField}
          sortOrder={pagination.sortOrder === "ASC" ? 1 : -1}
          className="p-datatable-gridlines"
          loading={loading}
        >
          <Column field="StateName" header={stateColumnHeader} sortable style={{ minWidth: "150px" }} />
          <Column field="DistrictName" header={districtColumnHeader} sortable style={{ minWidth: "150px" }} />
          <Column field="TownName" header={townColumnHeader} sortable style={{ minWidth: "150px" }} />
        </DataTable>

        <Dialog
          header="Add State"
          visible={showStateDialog}
          onHide={() => {
            setShowStateDialog(false);
            resetStateForm();
          }}
          modal
          className="popup"
          style={{ width: "30vw" }}
          footer={
            <Button label="Submit" icon="pi pi-check" onClick={() => submitHandler("state")} className="p-button-lg" />
          }
        >
          {renderDialogForm("state")}
        </Dialog>

        <Dialog
          header="Add District"
          visible={showDistrictDialog}
          onHide={() => {
            setShowDistrictDialog(false);
            resetDistrictForm();
          }}
          modal
          className="popup"
          style={{ width: "30vw" }}
          footer={
            <Button label="Submit" icon="pi pi-check" onClick={() => submitHandler("district")} className="p-button-lg" />
          }
        >
          {renderDialogForm("district")}
        </Dialog>

        <Dialog
          header="Add City/Town"
          visible={showCityDialog}
          onHide={() => {
            setShowCityDialog(false);
            resetCityForm();
          }}
          modal
          className="popup"
          style={{ width: "30vw" }}
          footer={
            <Button label="Submit" icon="pi pi-check" onClick={() => submitHandler("city")} className="p-button-lg" />
          }
        >
          {renderDialogForm("city")}
        </Dialog>
      </div>
    </div>
  );
};

export default LocationMaster;